import React from "react";
import { Link } from "react-router-dom";
import Flash from "flash"
import request from "request"
import { startCase } from "lodash"

import Icon from "Icon"
import Button from "Button"
import Confirm from "Confirm"
import Modal from "Modal"
import ClockInModal from "ClockInModal"
import TerminalAuth from "lib/auth/Terminal"

const CLOCK_POLL_INTERVAL_MS = 60000

const formatElapsed = (startAt, now) => {
  const diff = Math.max(0, Math.floor((now - new Date(startAt)) / 1000))
  const hours = Math.floor(diff / 3600)
  const minutes = Math.floor((diff % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export default class Navigation extends React.Component {
  state = {
    readerStatus: null,
    checkingReader: false,
    isIdentifyModalOpen: false,
    identificationString: "",
    isIdentifying: false,
    isClockModalOpen: false,
    clockedInEmployees: [],
    now: Date.now(),
  }

  componentDidMount() {
    this.fetchReaderStatus()
    this.fetchClockedInEmployees()
    this.pollInterval = setInterval(this.fetchClockedInEmployees, CLOCK_POLL_INTERVAL_MS)
    this.tickInterval = setInterval(() => this.setState({ now: Date.now() }), 1000)
  }

  componentWillUnmount() {
    clearInterval(this.pollInterval)
    clearInterval(this.tickInterval)
  }

  fetchClockedInEmployees = () => {
    // Don't poll without a terminal session — the request would 401 and the
    // global response interceptor would bounce the user to /login.
    if (!TerminalAuth.isAuthenticated()) return

    request.get("terminal/clock_in/current")
      .then(({ data }) => {
        if (data.success) {
          this.setState({ clockedInEmployees: data.employees || [] })
        }
      })
  }

  handleClockIn = (entry) => {
    this.setState((state) => {
      const withoutDuplicate = state.clockedInEmployees.filter((e) => e.timeLogId !== entry.timeLogId)
      return { clockedInEmployees: [...withoutDuplicate, entry] }
    })
  }

  handleClockOut = (timeLogId) => {
    this.setState((state) => ({
      clockedInEmployees: state.clockedInEmployees.filter((e) => e.timeLogId !== timeLogId),
    }))
  }

  renderNavItem = (text, link, icon) => {
    const isActive = window.location.pathname.includes(link)
    return(
      <Link to={link} className={`centered-link ${isActive ? "is-active" : "" }`}>
        <Icon icon={icon} className="sidebar-svg" />
        <span>{ text }</span>
      </Link>
    )
  }

  identifyReader = () => {
    const { terminal } = this.props
    const identificationString = Math.random().toString(36).substring(2, 8)
    this.setState({identificationString, isIdentifying: true})
    const req = request.post(`/terminals/${terminal.id}/reader/identify`, { identificationString })
    req.then(() => {
      this.setState({isIdentifyModalOpen: true})
    })
    req.catch(({response}) => {
      switch(response.data.error) {
        case "terminal_reader_offline":
          Flash.broadcast("error", "The reader is currently offline")
          break;
        case "terminal_reader_timeout":
          Flash.broadcast("error", "There was a timeout while identifying the reader. Please try again")
          break;
        default:
          Flash.broadcast("error", "There was an unknown error communicating with the reader")
      }
      this.setState({identificationString: null, isIdentifyModalOpen: false})
    })
    req.finally(() => {
      this.setState({isIdentifying: false})
    })
  }

  fetchReaderStatus = () => {
    this.setState({checkingReader: true})
    const req = request.get(`/terminals/${this.props.terminal.id}/reader/status`)
    req.then(({data}) => {
      const { readerStatus } = data
      this.setState({readerStatus, checkingReader: false})
    })
  }

  renderClockLabel() {
    const { clockedInEmployees, now } = this.state
    if (clockedInEmployees.length === 0) return null

    const sorted = [...clockedInEmployees].sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
    const primary = sorted[0]
    const others = sorted.length - 1

    return (
      <div className="mr-4 has-text-weight-bold" style={{fontSize: "1.1rem"}}>
        {primary.name} <span className="has-text-success">{formatElapsed(primary.startAt, now)}</span>
        {others > 0 && (
          <span className="has-text-grey ml-2">and {others} other{others === 1 ? "" : "s"}</span>
        )}
      </div>
    )
  }

  render() {
    const { terminal } = this.props
    const { readerStatus, checkingReader, clockedInEmployees } = this.state
    const hasEmployees = terminal.hasEmployees || false
    const anyClockedIn = clockedInEmployees.length > 0

    return(
      <div className="webapp-navbar no-print">
        <ClockInModal
          open={this.state.isClockModalOpen}
          onClose={() => this.setState({ isClockModalOpen: false })}
          employees={clockedInEmployees}
          onClockIn={this.handleClockIn}
          onClockOut={this.handleClockOut}
        />
        <Modal
          open={this.state.isIdentifyModalOpen}
        >
          <h3 className="title">Identifying Card Reader</h3>
          <h3 className="subtitle">Card Reader Displaying</h3>

          <table className="table mt-4 is-fullwidth">
            <tbody>
              <tr>
                <td>1</td>
                <td className="w-100">Reader for Terminal #{terminal.number}</td>
                <td>$0.00</td>
              </tr>
              <tr>
                <td>1</td>
                <td className="w-100">IDENTIFY {this.state.identificationString}</td>
                <td>$0.00</td>
              </tr>
              <tr>
                <td colSpan="2">Subtotal</td>
                <td>$0.01</td>
              </tr>
            </tbody>
          </table>
          <Button
            className=""
            text="Done"
            onClick={() => {
              const req = request.post(`/terminals/${terminal.id}/reader/clear`)
              this.setState({isIdentifyModalOpen: false, identificationString: ""})
            }}
          />
        </Modal>
        <div className="webapp-navbar-inner">
          <div className="left">
              <a href="/" className="brand">
                <img className="light-image" src="/images/stupid-logo.svg" alt="" />
              </a>
              <div className="separator"></div>
              <h1 id="webapp-page-title" className="title is-5" style={{whiteSpace: "nowrap"}}>
                Terminal #{terminal.number}
                <br/>
                <small className="subtitle is-size-6 text-no-wrap">{ terminal.store.name }</small>
              </h1>
          </div>
          <div className="center">
            <div className="centered-links">
              {
                // this.renderNavItem("Register", "/", "store")
              }
            </div>
          </div>
          <div className="right">
            <div className="navbar-item">
              <div className="buttons">
                {this.renderClockLabel()}
                {hasEmployees && (
                  <Button
                    className={`mr-2 ${anyClockedIn ? "is-warning" : "is-info"}`}
                    onClick={() => this.setState({ isClockModalOpen: true })}
                    text={anyClockedIn ? "Manage Clock" : "Clock In"}
                  />
                )}
                { readerStatus == "online" &&
                  <Button
                    className="mr-2 is-link"
                    onClick={this.identifyReader}
                    loading={this.state.isIdentifying}
                    disabled={this.state.isIdentifying}
                    text="Identify Reader"
                  />
                }
                { readerStatus == "online" &&
                  <Button
                    className="mr-2"
                    onClick={() => request.post(`/terminals/${terminal.id}/reader/clear`)}
                    text="Clear Reader"
                  />
                }
                { readerStatus &&
                  <>
                    <Button
                      onClick={this.fetchReaderStatus}
                      text="Check Reader"
                      loading={checkingReader}
                    />
                    <Button
                      className={`button has-text-weight-bold mr-4 ${readerStatus == "online" ? "is-success" : "is-danger"}`}
                      text={`Reader ${startCase(this.state.readerStatus)}`}
                    />
                  </>
                }
                <Button
                  className="button is-outlined is-danger navbar-item ml-6"
                  onClick={this.props.handleLogout}
                  text="Logout"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
